/**
 * Demandes d'absence : ce que la file des décisions, la file « À traiter » de l'accueil et
 * « Mes absences » partagent (décision du propriétaire du 15 octobre 2026).
 * Une demande répétée (congé chaque mercredi…) arrive groupée par l'API : une
 * ligne, du premier au dernier jour, avec son nombre de créneaux.
 */
import { useQuery } from '@tanstack/react-query';
import { api } from '../../core/api.js';

export interface AbsenceRequest {
  /** Premier créneau de la demande : il ouvre la demande et porte la décision. */
  id: string;
  kind: string;
  title: string;
  starts_at: string;
  ends_at: string;
  user_id: string;
  user_name?: string;
  approval_status: 'en_attente' | 'approuve' | 'refuse' | null;
  approved_at?: string | null;
  decision_reason?: string | null;
  occurrences?: number;
}

/** Demandes en attente, partagées avec l'onglet « À traiter » de la page Décisions. */
export function usePendingAbsenceRequests(enabled: boolean) {
  return useQuery({
    queryKey: ['absence-approvals', 'pending'],
    queryFn: () => api<AbsenceRequest[]>('/schedule-events/approvals?status=en_attente'),
    enabled,
    refetchInterval: 60_000,
  });
}

/**
 * « ven. 20 nov. 2026 », « lun. 2 nov. · 08:00–12:00 », « lun. 2 nov. – ven. 6 nov. 2026 ».
 * Une fin à minuit appartient au jour d'avant.
 */
export function absencePeriod(event: Pick<AbsenceRequest, 'starts_at' | 'ends_at'>): string {
  const start = new Date(event.starts_at);
  const end = new Date(event.ends_at);
  const exclusiveMidnight = end.getHours() === 0 && end.getMinutes() === 0
    && end.getSeconds() === 0 && end.getMilliseconds() === 0 && end > start;
  const displayedEnd = exclusiveMidnight ? new Date(end.getTime() - 1) : end;
  const startLabel = start.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' });
  const endLabel = displayedEnd.toLocaleDateString('fr-FR', {
    weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
  });
  if (exclusiveMidnight && start.toDateString() === displayedEnd.toDateString()) {
    return start.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
  }
  if (start.toDateString() === displayedEnd.toDateString()) {
    const time = (date: Date): string => date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    return `${startLabel} · ${time(start)}–${time(end)}`;
  }
  return `${startLabel} – ${endLabel}`;
}

/** « 3 créneaux » pour une demande répétée ; rien pour une demande d'un seul tenant. */
export function absenceRepeats(event: Pick<AbsenceRequest, 'occurrences'>): string {
  return (event.occurrences ?? 1) > 1 ? `${event.occurrences} créneaux` : '';
}

/** « Mes absences », dans le Menu : où chacun demande et suit ses absences. */
export const MY_ABSENCES_PATH = '/profil/absences';

/** Requêtes à rafraîchir après une décision, partout où une absence apparaît. */
export const ABSENCE_DECISION_QUERY_KEYS = [
  ['planning'], ['dashboard-exceptions'], ['pending-absence-approvals'], ['absence-approvals'],
  ['notifications'], ['notifications-unread'],
] as const;
